import type { LotteryEditPhase } from '@lotta/feature-lottery-rules';
import { useCreateLotteryMutation } from '@lotta/self-service/lottery-fe/data-access-state';
import { useCallback, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { LotteryForm } from './LotteryForm';
import { LotteryFormContext } from './lottery-form-context';
import { fromPayload } from './modules/transform';
import type { ReviewErrorTranslationKeys } from './review-configuration/ReviewConfiguration';
import { allModulesTemplate } from './templates';
import type { LotteryConfigData } from './types';
import { buildApiError } from './utils/error';
import { withLoadingValues } from './utils/with-loading-values';
import { filterTemplate } from './utils/filter-template';

const reviewTranslationKeys: ReviewErrorTranslationKeys = {
  // eslint-disable-next-line i18next/no-literal-string
  titleTranslationKey: 'general-error',
  // eslint-disable-next-line i18next/no-literal-string
  bodyTranslationKey: 'create-contest-inline-message-error',
};

export type CopyLotteryFormProps = {
  retailUnit: string;
  lotteryId: string;
  editMode: LotteryEditPhase;
  onLotteryCreated: (lotteryId: string) => void;
  onCancelled: () => void;
};

export const CopyLotteryForm: React.FC<CopyLotteryFormProps> = withLoadingValues(
  ({ values, retailUnit, editMode, onLotteryCreated, onCancelled }) => {
    const templateId = values.lottery.lotteryTemplateId;
    const modules = values.lottery.modules;
    const template = filterTemplate(modules, allModulesTemplate);
    const { lottery, translation } = values;

    // Clone the lottery object and reset the dates for occurrence
    const modifiedLottery = {
      ...lottery,
      occurrence: {
        ...lottery.occurrence,
        startDate: '',
        endDate: '',
      },
    };

    const modifiedValues = { lottery: modifiedLottery, translation };
    const data = fromPayload(modifiedValues, modules);

    const [createLottery, createLotteryResult] = useCreateLotteryMutation();
    const { isLoading: isCreateLotteryLoading } = createLotteryResult;

    const onFormSubmit: SubmitHandler<LotteryConfigData> = useCallback(
      (lotteryConfigData) => {
        createLottery({ retailUnit, ...lotteryConfigData });
      },
      [createLottery, retailUnit]
    );

    const resetAPIError = useCallback(() => {
      createLotteryResult.reset();
    }, [createLotteryResult]);

    useEffect(() => {
      if (createLotteryResult.isSuccess) {
        onLotteryCreated(createLotteryResult.data.id);
      }
    }, [createLotteryResult, onLotteryCreated]);

    return (
      <LotteryFormContext.Provider
        value={{ mode: editMode, template, result: { isCreateLotteryLoading } }}
      >
        <LotteryForm
          values={data}
          templateId={templateId}
          template={template}
          retailUnit={retailUnit}
          onFormSubmit={onFormSubmit}
          onSecondaryAction={onCancelled}
          error={
            createLotteryResult.isError
              ? buildApiError(createLotteryResult.isError, reviewTranslationKeys, resetAPIError)
              : undefined
          }
        />
      </LotteryFormContext.Provider>
    );
  }
);
